// Centralized cache system with multiple storage options
// Cache expiration time (60 minutes in milliseconds)
const CACHE_DURATION = 60 * 60 * 1000;

// Storage type options
type StorageType = 'local' | 'session' | 'memory';

// Cache item structure
interface CacheItem<T> {
    data: T;
    timestamp: number;
}

// Storage adapter interface
interface StorageAdapter {
    get<T>(key: string): CacheItem<T> | null;
    set<T>(key: string, item: CacheItem<T>): void;
    remove(key: string): void;
    getAllKeys(): string[];
    clear(prefix?: string): void;
    isAvailable(): boolean;
}

// In-memory cache storage
const memoryCache: Record<string, CacheItem<any>> = {};

// Memory storage adapter implementation
class MemoryStorageAdapter implements StorageAdapter {
    get<T>(key: string): CacheItem<T> | null {
        return memoryCache[key] || null;
    }

    set<T>(key: string, item: CacheItem<T>): void {
        memoryCache[key] = item;
    }

    remove(key: string): void {
        delete memoryCache[key];
    }

    getAllKeys(): string[] {
        return Object.keys(memoryCache);
    }

    clear(prefix?: string): void {
        if (prefix) {
            this.getAllKeys().forEach((key) => {
                if (key.startsWith(prefix)) {
                    this.remove(key);
                }
            });
        } else {
            this.getAllKeys().forEach((key) => {
                if (key.startsWith('cache-')) {
                    this.remove(key);
                }
            });
        }
    }

    isAvailable(): boolean {
        return true;
    }
}

// Browser storage adapter implementation
class BrowserStorageAdapter implements StorageAdapter {
    private storage: Storage;

    constructor(type: 'local' | 'session') {
        this.storage = type === 'local' ? localStorage : sessionStorage;
    }

    get<T>(key: string): CacheItem<T> | null {
        const item = this.storage.getItem(key);
        if (!item) return null;

        try {
            return JSON.parse(item) as CacheItem<T>;
        } catch (error) {
            console.warn('Failed to parse storage item:', error);
            return null;
        }
    }

    set<T>(key: string, item: CacheItem<T>): void {
        try {
            this.storage.setItem(key, JSON.stringify(item));
        } catch (error) {
            console.warn('Failed to save to storage:', error);
        }
    }

    remove(key: string): void {
        this.storage.removeItem(key);
    }

    getAllKeys(): string[] {
        const keys = [];
        for (let i = 0; i < this.storage.length; i++) {
            const key = this.storage.key(i);
            if (key) keys.push(key);
        }
        return keys;
    }

    clear(prefix?: string): void {
        // Create a list of keys to remove first (to avoid issues with changing indices during removal)
        const keysToRemove = this.getAllKeys().filter((key) => {
            if (prefix) {
                return key.startsWith(prefix);
            }
            return key.startsWith('cache-');
        });

        // Now remove the keys
        keysToRemove.forEach((key) => this.remove(key));
    }

    isAvailable(): boolean {
        try {
            const testKey = '__storage_test__';
            this.storage.setItem(testKey, testKey);
            this.storage.removeItem(testKey);
            return true;
        } catch (e) {
            return false;
        }
    }
}

// Factory function to get the appropriate storage adapter
function getStorageAdapter(type: StorageType): StorageAdapter {
    switch (type) {
        case 'local':
            return new BrowserStorageAdapter('local');
        case 'session':
            return new BrowserStorageAdapter('session');
        case 'memory':
        default:
            return new MemoryStorageAdapter();
    }
}

// Create a cache key from function name and parameters
function createCacheKey(functionName: string, params: any): string {
    return `cache-${functionName}-${JSON.stringify(params)}`;
}

// Generic caching wrapper function
export async function withCache<T>(
    functionName: string,
    params: any,
    apiCall: () => Promise<T>,
    options: {
        bypassCache?: boolean;
        storageType?: StorageType;
        cacheDuration?: number;
    } = {}
): Promise<T> {
    const {
        bypassCache = false,
        storageType = 'memory',
        cacheDuration = CACHE_DURATION,
    } = options;

    const cacheKey = createCacheKey(functionName, params);
    const now = Date.now();
    const storage = getStorageAdapter(storageType);

    // Check cache first (unless bypass is requested)
    if (!bypassCache) {
        const cachedItem = storage.get<T>(cacheKey);

        if (cachedItem && now - cachedItem.timestamp < cacheDuration) {
            console.log(`Using cached data for: ${functionName}`, params);
            return cachedItem.data;
        }
    }

    try {
        // Make the API call
        const response = await apiCall();

        // Cache the result
        const cacheItem: CacheItem<T> = {
            data: response,
            timestamp: now,
        };

        storage.set(cacheKey, cacheItem);
        return response;
    } catch (error) {
        console.error(`Error in ${functionName}:`, error);
        throw new Error(`Failed to execute ${functionName}`);
    }
}

// Helper function to clear cache
export function clearCache(
    options: {
        functionName?: string;
        params?: any;
        storageType?: StorageType;
    } = {}
): void {
    const { functionName, params, storageType = 'session' } = options;
    const storage = getStorageAdapter(storageType);

    if (functionName && params) {
        // Clear specific cache entry
        const key = createCacheKey(functionName, params);
        storage.remove(key);
    } else if (functionName) {
        // Clear all cache entries for a specific function
        const keyPrefix = `cache-${functionName}-`;
        storage.clear(keyPrefix);
    } else {
        // Clear all cache entries
        storage.clear();
    }
}

// Helper to check if storage is available
export function isStorageAvailable(type: StorageType): boolean {
    return getStorageAdapter(type).isAvailable();
}

// Helper to get cache stats
export function getCacheStats(storageType: StorageType = 'memory'): {
    totalEntries: number;
    oldestTimestamp: number | null;
    newestTimestamp: number | null;
    approximateSizeBytes?: number;
} {
    const storage = getStorageAdapter(storageType);
    const keys = storage.getAllKeys().filter((key) => key.startsWith('cache-'));

    let oldestTimestamp: number | null = null;
    let newestTimestamp: number | null = null;
    let totalSize = 0;

    keys.forEach((key) => {
        const item = storage.get<any>(key);
        if (item) {
            if (storageType !== 'memory') {
                // Rough estimate for browser storage: 2 bytes per character
                totalSize += JSON.stringify(item).length * 2;
            }

            const timestamp = item.timestamp;
            if (oldestTimestamp === null || timestamp < oldestTimestamp) {
                oldestTimestamp = timestamp;
            }
            if (newestTimestamp === null || timestamp > newestTimestamp) {
                newestTimestamp = timestamp;
            }
        }
    });

    const result: {
        totalEntries: number;
        oldestTimestamp: number | null;
        newestTimestamp: number | null;
        approximateSizeBytes?: number;
    } = {
        totalEntries: keys.length,
        oldestTimestamp,
        newestTimestamp,
    };

    if (storageType !== 'memory') {
        result.approximateSizeBytes = totalSize;
    }

    return result;
}
